import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setMenuOpen(false);
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-1 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
      isActive
        ? "text-primary-600"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="flex h-16 items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-3-fill text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg tracking-wide text-foreground-950">
              红桃视频
            </span>
            <span className="text-[10px] tracking-[0.2em] text-primary-500">
              在线观看
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-7 lg:flex">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={handleSearch}
          className="ml-auto hidden max-w-xs flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2 md:flex"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-search-line text-base text-foreground-500" />
          </span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            type="text"
            name="keyword"
            placeholder="搜索片名、导演、演员"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
          />
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开导航菜单"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 lg:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form
            onSubmit={handleSearch}
            className="mb-4 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-search-line text-base text-foreground-500" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              name="keyword"
              placeholder="搜索片名、导演、演员"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-sm font-medium ${
                    isActive
                      ? "bg-primary-50 text-primary-600"
                      : "text-foreground-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}